/**
 * The chequered ground plane.
 *
 * Generated into a canvas rather than loaded from an image, which keeps the build output
 * entirely self-contained and lets the check size be a stated measurement instead of a
 * property of some asset file.
 */

import {
  CanvasTexture,
  Mesh,
  MeshStandardMaterial,
  NearestFilter,
  PlaneGeometry,
  RepeatWrapping,
  SRGBColorSpace,
} from 'three';

/** Side length of one check, in metres. Small enough to read as a floor, not a chessboard. */
const CHECK_SIZE_M = 0.25;

/** How far the floor extends. Comfortably past any guardian boundary. */
const FLOOR_EXTENT_M = 12;

const LIGHT_CHECK = '#5a636d';
const DARK_CHECK = '#464e57';

function createChequerTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not get a 2D context to draw the floor texture.');

  const half = canvas.width / 2;
  context.fillStyle = LIGHT_CHECK;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = DARK_CHECK;
  context.fillRect(0, 0, half, half);
  context.fillRect(half, half, half, half);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;

  // Nearest keeps the check edges crisp underfoot, where a headset viewer is looking
  // most closely; mipmaps still handle the distance, so it does not shimmer.
  texture.magFilter = NearestFilter;

  // One texture tile spans two checks in each direction.
  const tiles = FLOOR_EXTENT_M / (CHECK_SIZE_M * 2);
  texture.repeat.set(tiles, tiles);

  return texture;
}

export function createFloor(): Mesh {
  const geometry = new PlaneGeometry(FLOOR_EXTENT_M, FLOOR_EXTENT_M);
  geometry.rotateX(-Math.PI / 2);

  const material = new MeshStandardMaterial({
    map: createChequerTexture(),
    roughness: 0.95,
    metalness: 0,
  });

  const floor = new Mesh(geometry, material);
  floor.name = 'floor';
  floor.receiveShadow = true;
  return floor;
}
